import { gql } from '@apollo/client';
import useStoreAndLanguageParams from '../../core/hooks/useStoreAndLanguageParams';
import useCancelableQuery from '../../core/hooks/apollo/useCancelableQuery';
import { useProductListOptionsContext } from '../components/ProductList/context/ProductListOptionsContext';

enum DefaultPriceRange {
    MIN_PRICE = 0,
    MAX_PRICE = 100000,
}

export interface PriceRangeParameters {
    skip?: boolean;
}

const usePriceRange = () => {
    const { store, language } = useStoreAndLanguageParams();

    const {
        filters: {
            categoryId,
            filters,
            vehicleId,
            bicycleId,
            search,
            preorderSeasonId,
            onSale,
            includePreorderSeasonId,
        },
        isFiltersLoading: skip,
    } = useProductListOptionsContext();

    const {
        data,
        loading,
        error: priceRangeError,
    } = useCancelableQuery<{
        products: {
            priceRange: {
                min: DefaultPriceRange.MIN_PRICE;
                max: DefaultPriceRange.MAX_PRICE;
            } | null;
        };
    }>(
        gql`
            query priceRange(
                $store: ID!
                $language: Language!
                $categoryId: ID
                $filters: [ProductListFilter!]
                $vehicleId: String
                $bicycleId: ID
                $search: String
                $preorderSeasonId: Int
                $includePreorderSeasonId: Int
                $onSale: Boolean
            ) {
                products(
                    store: $store
                    language: $language
                    categoryId: $categoryId
                    filters: $filters
                    vehicleId: $vehicleId
                    sparePartParentId: $bicycleId
                    search: $search
                    preorderSeasonId: $preorderSeasonId
                    includePreorderSeasonId: $includePreorderSeasonId
                    onSale: $onSale
                ) {
                    priceRange {
                        min
                        max
                    }
                }
            }
        `,
        {
            variables: {
                store,
                language,
                categoryId,
                filters,
                vehicleId,
                bicycleId,
                search,
                preorderSeasonId,
                onSale,
                includePreorderSeasonId,
            },
            skip,
        },
        true
    );

    if (priceRangeError) {
        throw priceRangeError;
    }

    return {
        min: Math.floor(
            data?.products.priceRange?.min ?? DefaultPriceRange.MIN_PRICE
        ),
        max: Math.ceil(
            data?.products.priceRange?.max ?? DefaultPriceRange.MAX_PRICE
        ),
        loading: (loading || !!skip) && !data,
    };
};

export default usePriceRange;
